import { UserRole } from '../types/index.js';

/** Stable permission ID prefix — matches design/docs/PERMISSIONS_AND_ENTITLEMENTS.md */
export const PERMISSION_ID_PREFIX = 'perm.' as const;

/** How a role relates to a permission in the matrix. */
export enum PermissionGrant {
  ALLOW = 'allow',
  DENY = 'deny',
  /** Granted with `.self`, `.linked`, `.assigned`, or `.team` scope restrictions. */
  SCOPED = 'scoped',
}

/** Where denial is enforced when a user lacks access. */
export enum PermissionEnforcementLayer {
  API = 'api',
  CLIENT = 'client',
  API_CLIENT = 'api+client',
  PLANNED = 'planned',
}

/** Account state modifiers applied on top of role grants. */
export enum AccountState {
  ANON = 'ANON',
  NO_TEAM = 'NO_TEAM',
  IN_TEAM = 'IN_TEAM',
}

export const PERMISSION_MATRIX_ROLES = [
  UserRole.TEAM_OWNER,
  UserRole.COACH,
  UserRole.MECHANIC,
  UserRole.DRIVER,
  UserRole.PARENT,
] as const;

export type PermissionRoleMatrix = Record<(typeof PERMISSION_MATRIX_ROLES)[number], PermissionGrant>;

export type PermissionDefinition = {
  id: string;
  displayName: string;
  domain: PermissionDomain;
  description?: string;
  enforcement: PermissionEnforcementLayer;
  roles: PermissionRoleMatrix;
  scopeNotes?: string;
  clientHelper?: string;
  apiRoute?: string;
};

export type PermissionDomain =
  | 'auth'
  | 'onboarding'
  | 'team'
  | 'drivers'
  | 'vehicles'
  | 'data'
  | 'telemetry'
  | 'circuits'
  | 'maintenance'
  | 'settings'
  | 'navigation';

export type ClientCapabilityDefinition = {
  id: string;
  helper: string;
  sourceFile: string;
  description: string;
  permissionIds: string[];
  roles: Record<UserRole, boolean>;
};

/** Documents how effective access is computed today (role-first, not JWT permission sets). */
export const PERMISSION_STORAGE_MODEL = {
  summary:
    'Effective access = (role permission matrix) ∩ (account state) ∩ (plan entitlements — spec only today).',
  roleField: 'User.role: UserRole',
  roleStorage: 'Persisted on the user record at registration or invite accept; not a separate permission set document.',
  permissionSetStorage:
    'There is no per-user permission array in the database yet. Permissions are derived at runtime from UserRole via shared matrices and client/API helper functions.',
  accountStateRules: {
    [AccountState.ANON]: 'Auth-only permissions (register, login, complete invite).',
    [AccountState.NO_TEAM]: 'Onboarding permissions (create team, join request) without team-scoped ops.',
    [AccountState.IN_TEAM]: 'Full role matrix applies; scoped permissions resolve via driver/kart linkage.',
  },
  clientEvaluation:
    'Web/mobile call canManage*() helpers in roles.ts and driverAccess.ts — they mirror subsets of the canonical permission IDs.',
  apiEvaluation:
    'API routes use requireRole(...UserRole) middleware plus scoped access checks (canAccessDriver, resolveKartAccess, etc.).',
  futureJwt:
    'Recommended: attach permissions: PaddiqPermission[] to JWT or GET /auth/me to avoid duplicating matrices in clients.',
  sourceDocs: 'design/docs/PERMISSIONS_AND_ENTITLEMENTS.md',
  sourceEnum: 'packages/shared/src/types/index.ts → UserRole',
  sourceRegistry: 'packages/shared/src/permissions/index.ts',
} as const;

function grants(
  owner: PermissionGrant,
  coach: PermissionGrant,
  mechanic: PermissionGrant,
  driver: PermissionGrant,
  parent: PermissionGrant,
): PermissionRoleMatrix {
  return {
    [UserRole.TEAM_OWNER]: owner,
    [UserRole.COACH]: coach,
    [UserRole.MECHANIC]: mechanic,
    [UserRole.DRIVER]: driver,
    [UserRole.PARENT]: parent,
  };
}

const all = PermissionGrant.ALLOW;
const none = PermissionGrant.DENY;
const scoped = PermissionGrant.SCOPED;

export const PADDIQ_PERMISSIONS: PermissionDefinition[] = [
  {
    id: 'perm.auth.session.read.self',
    displayName: 'Read own session',
    domain: 'auth',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'GET /auth/me',
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.auth.profile.update.self',
    displayName: 'Update own profile',
    domain: 'auth',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'PATCH /auth/me',
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.onboarding.team.create',
    displayName: 'Create team workspace',
    domain: 'onboarding',
    scopeNotes: 'NO_TEAM only',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canCreateTeamWorkspace()',
    apiRoute: 'POST /teams/current',
    roles: grants(all, all, none, none, all),
  },
  {
    id: 'perm.onboarding.team.join_request',
    displayName: 'Request to join team',
    domain: 'onboarding',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'POST /teams/join-requests',
    roles: grants(none, none, none, all, all),
  },
  {
    id: 'perm.team.profile.update',
    displayName: 'Edit team profile',
    domain: 'team',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canManageTeam()',
    apiRoute: 'PUT /teams/current',
    roles: grants(all, all, none, none, none),
  },
  {
    id: 'perm.team.members.list',
    displayName: 'List team members',
    domain: 'team',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canViewTeamMembers()',
    apiRoute: 'GET /teams/current/members',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.team.members.create.staff',
    displayName: 'Create staff account',
    domain: 'team',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'POST /teams/current/members',
    roles: grants(all, all, none, none, none),
  },
  {
    id: 'perm.team.invitations.create',
    displayName: 'Send invitation',
    domain: 'team',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'POST /teams/current/invitations',
    roles: grants(all, all, none, none, none),
  },
  {
    id: 'perm.drivers.profile.create',
    displayName: 'Create driver profile',
    domain: 'drivers',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canManageDrivers()',
    apiRoute: 'POST /drivers',
    roles: grants(all, all, none, none, none),
  },
  {
    id: 'perm.drivers.roster.list.team',
    displayName: 'List all drivers',
    domain: 'drivers',
    scopeNotes: '.team',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'GET /drivers',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.drivers.roster.list.self',
    displayName: 'List own driver profile',
    domain: 'drivers',
    scopeNotes: '.self',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'filterDriversForUser()',
    roles: grants(none, none, none, all, none),
  },
  {
    id: 'perm.drivers.roster.list.linked',
    displayName: 'List linked drivers',
    domain: 'drivers',
    scopeNotes: '.linked',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'filterDriversForUser()',
    roles: grants(none, none, none, none, all),
  },
  {
    id: 'perm.karts.fleet.create',
    displayName: 'Add vehicle',
    domain: 'vehicles',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canManageKarts()',
    apiRoute: 'POST /karts',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.karts.fleet.update',
    displayName: 'Edit vehicle',
    domain: 'vehicles',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'PUT /karts/:id',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.karts.fleet.list.team',
    displayName: 'List all vehicles',
    domain: 'vehicles',
    scopeNotes: '.team',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'GET /karts',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.karts.fleet.list.assigned',
    displayName: 'List assigned vehicles',
    domain: 'vehicles',
    scopeNotes: '.assigned',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'filterKartsForDriver()',
    roles: grants(none, none, none, scoped, scoped),
  },
  {
    id: 'perm.karts.fleet.read.assigned',
    displayName: 'View assigned vehicle',
    domain: 'vehicles',
    enforcement: PermissionEnforcementLayer.API,
    roles: grants(none, none, none, scoped, scoped),
  },
  {
    id: 'perm.data.upload.mychron',
    displayName: 'Upload MyChron files',
    domain: 'data',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canManageDataFiles()',
    apiRoute: 'POST /data-files/mychron',
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.data.files.list.team',
    displayName: 'List all session files',
    domain: 'data',
    scopeNotes: '.team',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'GET /data-files',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.data.files.list.scoped',
    displayName: 'List accessible session files',
    domain: 'data',
    scopeNotes: 'own + linked',
    enforcement: PermissionEnforcementLayer.API,
    roles: grants(none, none, none, all, all),
  },
  {
    id: 'perm.import.trackside.web',
    displayName: 'Trackside import (web)',
    domain: 'data',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'canUseTracksideImport()',
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.telemetry.sessions.view',
    displayName: 'Open telemetry workspace',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.CLIENT,
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.telemetry.filter.drivers.all',
    displayName: 'Filter sessions by any driver',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'canSelectAnyTeamDriver()',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.telemetry.filter.drivers.self',
    displayName: 'Locked to own driver',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'isDriverSelectionLocked()',
    roles: grants(none, none, none, all, none),
  },
  {
    id: 'perm.telemetry.filter.drivers.linked',
    displayName: 'Filter linked drivers',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'filterDriversForUser()',
    roles: grants(none, none, none, none, all),
  },
  {
    id: 'perm.telemetry.laps.compare',
    displayName: 'Lap compare overlay',
    domain: 'telemetry',
    scopeNotes: 'Plan gate: advancedTelemetry (spec)',
    enforcement: PermissionEnforcementLayer.CLIENT,
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.telemetry.context.update',
    displayName: 'Update session assignment',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.API_CLIENT,
    clientHelper: 'canUpdateTelemetrySessionContext()',
    apiRoute: 'PUT /telemetry/sessions/:key/context',
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.telemetry.context.driver.assign.any',
    displayName: 'Assign any team driver to session',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.API,
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.telemetry.segments.create',
    displayName: 'Save named segment',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'POST /telemetry/segments',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.telemetry.tracks.create',
    displayName: 'Create track definition',
    domain: 'telemetry',
    enforcement: PermissionEnforcementLayer.API,
    apiRoute: 'POST /telemetry/tracks',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.circuits.contributions.review',
    displayName: 'Accept/reject/merge circuit',
    domain: 'circuits',
    enforcement: PermissionEnforcementLayer.API,
    roles: grants(all, all, none, none, none),
  },
  {
    id: 'perm.circuits.contributions.draft.save',
    displayName: 'Save circuit draft',
    domain: 'circuits',
    enforcement: PermissionEnforcementLayer.API,
    roles: grants(all, all, all, all, none),
  },
  {
    id: 'perm.maintenance.queue.manage',
    displayName: 'Manage maintenance queue',
    domain: 'maintenance',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'canManageKarts()',
    roles: grants(all, all, all, none, none),
  },
  {
    id: 'perm.settings.account.manage',
    displayName: 'Account settings',
    domain: 'settings',
    enforcement: PermissionEnforcementLayer.CLIENT,
    roles: grants(all, all, all, all, all),
  },
  {
    id: 'perm.nav.web.vehicles',
    displayName: 'Web: Vehicles nav',
    domain: 'navigation',
    enforcement: PermissionEnforcementLayer.CLIENT,
    clientHelper: 'canViewFleet()',
    roles: grants(all, all, all, scoped, scoped),
  },
];

export const CLIENT_CAPABILITY_HELPERS: ClientCapabilityDefinition[] = [
  {
    id: 'cap.manage-team',
    helper: 'canManageTeam',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Team admin UI — profile, invitations, account creation',
    permissionIds: ['perm.team.profile.update', 'perm.team.accounts.create.ui'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: false,
      [UserRole.DRIVER]: false,
      [UserRole.PARENT]: false,
    },
  },
  {
    id: 'cap.manage-drivers',
    helper: 'canManageDrivers',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Create and release driver profiles',
    permissionIds: ['perm.drivers.profile.create', 'perm.drivers.profile.release'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: false,
      [UserRole.DRIVER]: false,
      [UserRole.PARENT]: false,
    },
  },
  {
    id: 'cap.view-team-members',
    helper: 'canViewTeamMembers',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Staff roster list',
    permissionIds: ['perm.team.members.list'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: true,
      [UserRole.DRIVER]: false,
      [UserRole.PARENT]: false,
    },
  },
  {
    id: 'cap.manage-karts',
    helper: 'canManageKarts',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Vehicle CRUD and service logging',
    permissionIds: ['perm.karts.fleet.create', 'perm.karts.fleet.update', 'perm.maintenance.queue.manage'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: true,
      [UserRole.DRIVER]: false,
      [UserRole.PARENT]: false,
    },
  },
  {
    id: 'cap.view-fleet',
    helper: 'canViewFleet',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Vehicles navigation and read access',
    permissionIds: ['perm.nav.web.vehicles', 'perm.karts.fleet.read.assigned'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: true,
      [UserRole.DRIVER]: true,
      [UserRole.PARENT]: true,
    },
  },
  {
    id: 'cap.manage-data-files',
    helper: 'canManageDataFiles',
    sourceFile: 'apps/web/src/lib/roles.ts',
    description: 'Upload MyChron session files',
    permissionIds: ['perm.data.upload.mychron'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: true,
      [UserRole.DRIVER]: true,
      [UserRole.PARENT]: true,
    },
  },
  {
    id: 'cap.select-any-driver',
    helper: 'canSelectAnyTeamDriver',
    sourceFile: 'apps/web/src/lib/driverAccess.ts',
    description: 'Telemetry session filter — all drivers',
    permissionIds: ['perm.telemetry.filter.drivers.all'],
    roles: {
      [UserRole.TEAM_OWNER]: true,
      [UserRole.COACH]: true,
      [UserRole.MECHANIC]: true,
      [UserRole.DRIVER]: false,
      [UserRole.PARENT]: false,
    },
  },
  {
    id: 'cap.driver-selection-locked',
    helper: 'isDriverSelectionLocked',
    sourceFile: 'apps/web/src/lib/driverAccess.ts',
    description: 'Telemetry locked to own driver profile',
    permissionIds: ['perm.telemetry.filter.drivers.self'],
    roles: {
      [UserRole.TEAM_OWNER]: false,
      [UserRole.COACH]: false,
      [UserRole.MECHANIC]: false,
      [UserRole.DRIVER]: true,
      [UserRole.PARENT]: false,
    },
  },
];

export const PERMISSION_DOMAINS: PermissionDomain[] = [
  'auth',
  'onboarding',
  'team',
  'drivers',
  'vehicles',
  'data',
  'telemetry',
  'circuits',
  'maintenance',
  'settings',
  'navigation',
];

export function permissionGrantLabel(grant: PermissionGrant) {
  switch (grant) {
    case PermissionGrant.ALLOW:
      return '✓';
    case PermissionGrant.SCOPED:
      return '◐';
    default:
      return '—';
  }
}

export function permissionGrantColor(grant: PermissionGrant): 'success' | 'warning' | 'default' {
  switch (grant) {
    case PermissionGrant.ALLOW:
      return 'success';
    case PermissionGrant.SCOPED:
      return 'warning';
    default:
      return 'default';
  }
}

export function countPermissionGrantsForRole(role: UserRole) {
  let allowed = 0;
  let scoped = 0;
  let denied = 0;

  for (const permission of PADDIQ_PERMISSIONS) {
    const grant = permission.roles[role];
    if (grant === PermissionGrant.ALLOW) {
      allowed += 1;
    } else if (grant === PermissionGrant.SCOPED) {
      scoped += 1;
    } else {
      denied += 1;
    }
  }

  return { allowed, scoped, denied, total: PADDIQ_PERMISSIONS.length };
}

export function formatUserRoleCode(role: UserRole) {
  switch (role) {
    case UserRole.TEAM_OWNER:
      return 'OWNER';
    case UserRole.COACH:
      return 'COACH';
    case UserRole.MECHANIC:
      return 'MECH';
    case UserRole.DRIVER:
      return 'DRIVER';
    case UserRole.PARENT:
      return 'PARENT';
    default:
      return role;
  }
}

export function formatUserRoleLabel(role: UserRole) {
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
